import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Close, RssFeed } from '@mui/icons-material';

import { Box } from 'src/components/Box';
import { Input } from 'src/components/Input';
import { Button } from 'src/components/Button';
import { Typography } from 'src/components/Typography';
import { BUTTON_COLOR, BUTTON_SIZE, BUTTON_VARIANT } from 'src/components/Button/types';
import { TYPOGRAPHY_COLORS, TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { INPUT_SIZE } from 'src/components/Input/types';
import { ParseJobsFromFeedProps } from './ParseJobsFromFeed.types';
import typography from 'src/theme/base/typography';

export const ParseJobsFromFeed: React.FC<ParseJobsFromFeedProps> = ({ feedImport }) => {
  const { t } = useTranslation();

  const { size: fontSize } = typography;

  const {
    parsedJobs,
    selectedIndexes,
    hasJobs,
    isFetchingFeed,
    isFeedDialogOpen,
    totalSelected,
    toggleJobSelection,
    selectAll,
    deselectAll,
    startImporting,
    resetFeed,
    handleUrlChange,
    url,
    error,
    handleClose,
    handleSubmit,
  } = feedImport;

  const LoadFeedLabel = isFetchingFeed ? (
    <CircularProgress size={20} color="inherit" />
  ) : (
    <Trans>jobPage.feedDialog.loadFeed</Trans>
  );

  const SelectJobsLabel = (allSelected: boolean) =>
    allSelected ? (
      <Trans>jobPage.feedDialog.deselectAll</Trans>
    ) : (
      <Trans>jobPage.feedDialog.selectAll</Trans>
    );

  const allSelected = selectedIndexes.size === parsedJobs.length && parsedJobs.length > 0;

  return (
    <Dialog
      hideBackdrop={true}
      open={isFeedDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
            <Trans>jobPage.feedDialog.title</Trans>
          </Typography>
          <Close onClick={handleClose} sx={{ cursor: 'pointer' }} />
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2, pb: 3 }}>
        {!hasJobs && (
          <Box>
            <Typography
              color={TYPOGRAPHY_COLORS.TEXT}
              fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}
              fontSize={fontSize.sm}
              mb={2}>
              <Trans>jobPage.feedDialog.description</Trans>
            </Typography>

            <Input
              size={INPUT_SIZE.MEDIUM}
              icon={{ component: <RssFeed />, direction: 'left', padding: 5 }}
              placeholder={t('jobPage.feedDialog.placeholder')}
              value={url}
              onChange={handleUrlChange}
              error={!!error}
              helperText={error || ' '}
              disabled={isFetchingFeed}
            />
          </Box>
        )}

        {hasJobs && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
                <Trans
                  i18nKey="jobPage.feedDialog.foundJobs"
                  values={{ count: parsedJobs.length }}
                />
              </Typography>
              <Button
                variant={BUTTON_VARIANT.TEXT}
                size={BUTTON_SIZE.SMALL}
                color={BUTTON_COLOR.INFO}
                onClick={allSelected ? deselectAll : selectAll}>
                {SelectJobsLabel(allSelected)}
              </Button>
            </Box>

            <Divider />

            <List
              sx={{
                maxHeight: 320,
                overflow: 'auto',
              }}>
              {parsedJobs.map((job, index) => {
                const isSelected = selectedIndexes.has(index);
                const jobName = job.roleData.name;
                const jobLocation = job.roleData.location || job.roleData.city || '';

                return (
                  <ListItem key={index} disablePadding>
                    <ListItemButton onClick={() => toggleJobSelection(index)} dense>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemText
                        primary={jobName}
                        secondary={jobLocation || undefined}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {hasJobs && (
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.INFO}
            size={BUTTON_SIZE.MEDIUM}
            disabled={isFetchingFeed && parsedJobs.length === 0}
            onClick={resetFeed}>
            <Trans>jobPage.feedDialog.deleteImportedJobs</Trans>
          </Button>
        )}

        {!hasJobs && (
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            size={BUTTON_SIZE.MEDIUM}
            onClick={handleSubmit}
            disabled={isFetchingFeed || !url.trim() || !!error}>
            {LoadFeedLabel}
          </Button>
        )}

        {hasJobs && (
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.INFO}
            size={BUTTON_SIZE.MEDIUM}
            onClick={startImporting}
            disabled={totalSelected === 0}>
            <Trans i18nKey="jobPage.feedDialog.importSelected" values={{ count: totalSelected }} />
          </Button>
        )}

        <Button
          onClick={handleClose}
          disabled={isFetchingFeed}
          variant={BUTTON_VARIANT.OUTLINED}
          color={BUTTON_COLOR.DARK}
          size={BUTTON_SIZE.MEDIUM}>
          <Trans>jobPage.feedDialog.cancel</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
